import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { parseISO, startOfDay } from 'date-fns'
import { isMobile } from 'react-device-detect'
import { FlexColumn } from '@/atoms/FlexContainers'
import { UploadIcon } from '@/atoms/Icons/UploadIcon'
import { TitleXS } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { TicketsMainFooter } from '@/organisms/Footer/TicketsMainFooter'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { isProductionEnvironment } from '@/utils/environment-utils'
import { useTranslate } from '@/utils/translate/translate-client'
import { AddressSearch } from '@/views/TicketCheckoutViews/ShowtimesView/components/AddressSearch'
import { Calendar } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/Calendar'
import { ComingSoon } from '@/views/TicketCheckoutViews/ShowtimesView/components/ComingSoon'
import FandangoRedirect from '@/views/TicketCheckoutViews/ShowtimesView/components/FandangoRedirect'
import TicketBannerSection from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketBannerSection'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TicketsPayItForwardAd } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPayItForwardAd'
import { FreeTicketModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/FreeTicketModal'
import { useTicketsPromoContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import Venues from '@/views/TicketCheckoutViews/ShowtimesView/components/Venues'
import TicketPromoModals from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TicketPromoModals'
import { LimitedDateModalProvider } from '@/views/TicketCheckoutViews/ShowtimesView/contexts/LimitedDateModalContext'
import { useFindQueryVenue } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useFindQueryVenue'
import { useVenueSearch } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch'
import { Coordinates } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch/useVenueSearch'
import { InitializedReservation } from '../initReservation'
import { BasicHero } from './BasicHero'

interface TicketSearchProps {
  theatricalSlug: string
  theatricalRelease: NonNullable<TheatricalReleaseResult>
  scrollToTop: () => void
  calendarRef: React.RefObject<HTMLDivElement>
  reservation: InitializedReservation
  isEditReservationFlow?: boolean
  showOverlayVideoExperiment?: boolean
}

export const ShowtimesLayout = ({
  theatricalRelease,
  calendarRef,
  theatricalSlug,
  isEditReservationFlow = false,
  reservation,
  scrollToTop,
}: TicketSearchProps) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('tickets')
  const searchScrollContainer = useRef<HTMLDivElement>(null)
  const { openSharingModal, handleMobileShare, isFreeTicketPromo, earlyDateToTry, basicPageExperimentEnabled } =
    useTicketsContext()
  const { venue: showtimeSelectedVenue } = useTicketsPromoContext()
  const contentWrapperRef = useRef<HTMLDivElement>(null)
  const [isPremiereLocation, setIsPremiereLocation] = useState(false)
  const isNotProduction = !isProductionEnvironment()

  const scrollToContentTop = useCallback(() => {
    contentWrapperRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const releaseDate = useMemo(() => {
    const rawDate = theatricalRelease?.region?.releaseDate
    if (!rawDate) return new Date()

    try {
      const dateOnly = rawDate.includes('T') ? rawDate.split('T')[0] || '' : rawDate
      return startOfDay(parseISO(dateOnly))
    } catch {
      return new Date()
    }
  }, [theatricalRelease?.region?.releaseDate])

  const {
    searchDate,
    handleDateChange,
    handleAddressChange,
    earliestDate,
    specialDate,
    venues,
    loading,
    hasSearchedOnce,
    defaultAddressString,
    error,
  } = useVenueSearch({
    releaseDate: releaseDate,
    theatricalSlug,
    countryCode: theatricalRelease?.region?.countryCode ?? 'us',
    earlyDateToTry,
  })

  useEffect(() => {
    if (
      defaultAddressString &&
      (defaultAddressString.includes('Lehi') || defaultAddressString === 'Lehi, UT, USA') &&
      theatricalSlug === slugs.david
    ) {
      setIsPremiereLocation(true)
    }
  }, [defaultAddressString, theatricalSlug])

  const { venue } = useFindQueryVenue({ venues })

  const goToStagingTheater = useCallback(() => {
    const dallasText = 'Dallas, Texas'
    const dallasCoordinates: Coordinates = {
      latitude: 32.7767,
      longitude: -96.797,
    }
    handleAddressChange(dallasCoordinates, dallasText)
  }, [handleAddressChange])

  const theatricalReleaseName = theatricalRelease.title ?? ''
  const shouldShowTicketsComingSoon = theatricalRelease?.region?.ticketsStatus === 'COMING_SOON'

  const handleUserDateChange = useCallback(
    (date: Date) => {
      handleDateChange(date)
    },
    [handleDateChange],
  )

  return (
    <LimitedDateModalProvider theatricalSlug={theatricalSlug} isPremiereLocation={isPremiereLocation}>
      <main
        ref={searchScrollContainer}
        className={classNames(
          'w-full gap-y-2 overflow-x-hidden lg:overflow-y-scroll',
          isDarkMode ? 'bg-core-gray-950' : 'bg-core-gray-100',
        )}
      >
        {basicPageExperimentEnabled && (
          <BasicHero
            theatricalSlug={theatricalSlug}
            bgVideoUrl="https://content.angelstudios.com/david/David_tLoopingVideo_l-en_wNOCAPS_16x9_f23_y20251013_iDG.mp4"
            fallbackImageUrl="https://images.angelstudios.com/image/upload/v1760542947/theatrical/david/david-hand-img-01.webp"
            logoUrl="/v1764102078/theatrical/david/david-logo-color-800m.webp"
          />
        )}
        <h1 className="sr-only">
          {t('projectTicketsAndShowtimes', '{{projectName}} Tickets & Showtimes', {
            projectName: theatricalRelease?.title,
          })}
        </h1>
        <div
          className={classNames('flex min-h-[50vh] flex-col gap-4 px-4', {
            'py-10 md:py-12 lg:py-16 md:px-10 lg:px-12 max-w-[1440px] mx-auto !gap-6 md:!gap-8':
              basicPageExperimentEnabled,
          })}
        >
          <div ref={contentWrapperRef} className={classNames(!basicPageExperimentEnabled && 'pt-6')}>
            <FlexColumn className="gap-2">
              <div className="flex flex-row flex-wrap items-end justify-between">
                <div className="flex items-center gap-3">
                  <label htmlFor="search">
                    <TitleXS weight="bold" className="text-core-gray-950">
                      {t('enterCityStateZipV3', '   Search Location')}
                    </TitleXS>
                  </label>
                  {isNotProduction && (
                    <button
                      onClick={goToStagingTheater}
                      className="rounded-md bg-core-gray-900 px-3 py-1 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-core-gray-700 focus:ring-offset-1 hover:bg-core-gray-800"
                    >
                      Go to staging venue
                    </button>
                  )}
                </div>
                <button className="flex flex-row gap-1" onClick={isMobile ? handleMobileShare : openSharingModal}>
                  <TitleXS weight="semibold" className="my-auto cursor-pointer text-sm text-core-gray-950">
                    {t('share', 'Share')}
                  </TitleXS>
                  <UploadIcon color="core-gray-950" size={18} />
                </button>
              </div>
              <AddressSearch
                countryCode={theatricalRelease?.region?.countryCode ?? 'us'}
                onChange={(coordinates, searchString) => {
                  handleAddressChange(coordinates, searchString)
                }}
                defaultAddressString={defaultAddressString}
                scrollToVenues={scrollToTop}
                scrollToContentTop={scrollToContentTop}
              />
            </FlexColumn>

            <Calendar
              releaseDate={releaseDate.toISOString()}
              className={classNames('-mx-4 mt-10 min-h-[120px]', {
                '!mb-2 md:!mb-4': basicPageExperimentEnabled,
              })}
              disabled={false}
              onChange={handleUserDateChange}
              selectedDate={searchDate}
              startDate={earliestDate}
              specialDate={specialDate}
              scrollToTop={scrollToTop}
              calendarRef={calendarRef}
              theatricalSlug={theatricalSlug}
            />

            <TicketBannerSection
              reservation={reservation}
              isEditReservationFlow={isEditReservationFlow}
              theatricalRelease={theatricalRelease}
            />
          </div>
          {shouldShowTicketsComingSoon ? (
            <ComingSoon slug={theatricalRelease?.theatricalSlug} />
          ) : (
            <Venues
              venues={venues}
              theatricalRelease={theatricalRelease}
              isLoading={loading || !hasSearchedOnce}
              reservation={reservation}
            />
          )}
        </div>
        <TicketsMainFooter
          className="-mx-4 mt-6"
          slug={theatricalRelease.projectSlug}
          projectName={theatricalReleaseName}
        />
      </main>
      <TicketsPayItForwardAd
        theatricalSlug={theatricalSlug}
        className="experiment-hidden justify-center text-center md:hidden"
      />
      <FandangoRedirect
        fandangoUrl={theatricalRelease?.region?.ticketsEmergencyRedirectUrl ?? ''}
        shouldRedirectToFandango={!!error && !shouldShowTicketsComingSoon}
      />

      {isFreeTicketPromo && (
        <FreeTicketModal
          maxTickets={theatricalRelease?.region?.freeTicketRequestAmount ?? 1}
          venue={showtimeSelectedVenue ?? venue}
        />
      )}

      <TicketPromoModals theatricalSlug={theatricalSlug} />
    </LimitedDateModalProvider>
  )
}
